// Animated line waves behind the brand panel that `auth_split/1` renders on the
// sign-in and onboarding screens: a port of the prototype's
// ogl WebGL shader (martide-jobs-v2 src/components/martide/LineWaves.tsx) with
// the parameters its DesktopAuthLayout uses — the component defaults plus three
// theme colours read from CSS custom properties when the scene is built.
//
// Without WebGL, or under `prefers-reduced-motion: reduce`, it draws nothing
// and the panel's static glow stays as the fallback. Mount it on an element with
// `phx-update="ignore"` so LiveView patches leave the canvas alone.
import { Renderer, Program, Mesh, Triangle } from "ogl";

const VERTEX = `
attribute vec2 uv;
attribute vec2 position;
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position, 0, 1);
}
`;

const FRAGMENT = `
precision highp float;

uniform float uTime;
uniform vec3 uResolution;
uniform float uSpeed;
uniform float uInnerLines;
uniform float uOuterLines;
uniform float uWarpIntensity;
uniform float uRotation;
uniform float uEdgeFadeWidth;
uniform float uColorCycleSpeed;
uniform float uBrightness;
uniform vec3 uColor1;
uniform vec3 uColor2;
uniform vec3 uColor3;
uniform vec2 uMouse;
uniform float uMouseInfluence;
uniform bool uEnableMouse;

#define HALF_PI 1.5707963

float hashF(float n) {
  return fract(sin(n * 127.1) * 43758.5453123);
}

float smoothNoise(float x) {
  float i = floor(x);
  float f = fract(x);
  float u = f * f * (3.0 - 2.0 * f);
  return mix(hashF(i), hashF(i + 1.0), u);
}

float displaceA(float coord, float t) {
  float result = sin(coord * 2.123) * 0.2;
  result += sin(coord * 3.234 + t * 4.345) * 0.1;
  result += sin(coord * 0.589 + t * 0.934) * 0.5;
  return result;
}

float displaceB(float coord, float t) {
  float result = sin(coord * 1.345) * 0.3;
  result += sin(coord * 2.734 + t * 3.345) * 0.2;
  result += sin(coord * 0.189 + t * 0.934) * 0.3;
  return result;
}

vec2 rotate2D(vec2 p, float angle) {
  float c = cos(angle);
  float s = sin(angle);
  return vec2(p.x * c - p.y * s, p.x * s + p.y * c);
}

void main() {
  vec2 coords = gl_FragCoord.xy / uResolution.xy;
  coords = coords * 2.0 - 1.0;
  coords = rotate2D(coords, uRotation);

  float halfT = uTime * uSpeed * 0.5;
  float fullT = uTime * uSpeed;

  float mouseWarp = 0.0;
  if (uEnableMouse) {
    vec2 mPos = rotate2D(uMouse * 2.0 - 1.0, uRotation);
    float mDist = length(coords - mPos);
    mouseWarp = uMouseInfluence * exp(-mDist * mDist * 4.0);
  }

  float warpAx = coords.x + displaceA(coords.y, halfT) * uWarpIntensity + mouseWarp;
  float warpAy = coords.y - displaceA(coords.x * cos(fullT) * 1.235, halfT) * uWarpIntensity;
  float warpBx = coords.x + displaceB(coords.y, halfT) * uWarpIntensity + mouseWarp;
  float warpBy = coords.y - displaceB(coords.x * sin(fullT) * 1.235, halfT) * uWarpIntensity;

  vec2 fieldA = vec2(warpAx, warpAy);
  vec2 fieldB = vec2(warpBx, warpBy);
  vec2 blended = mix(fieldA, fieldB, mix(fieldA, fieldB, 0.5));

  float fadeTop = smoothstep(uEdgeFadeWidth, uEdgeFadeWidth + 0.4, blended.y);
  float fadeBottom = smoothstep(-uEdgeFadeWidth, -(uEdgeFadeWidth + 0.4), blended.y);
  float vMask = 1.0 - max(fadeTop, fadeBottom);

  float tileCount = mix(uOuterLines, uInnerLines, vMask);
  float scaledY = blended.y * tileCount;
  float nY = smoothNoise(abs(scaledY));

  float ridge = pow(
    step(abs(nY - blended.x) * 2.0, HALF_PI) * cos(2.0 * (nY - blended.x)),
    5.0
  );

  float lines = 0.0;
  for (float i = 1.0; i < 3.0; i += 1.0) {
    lines += pow(max(fract(scaledY), fract(-scaledY)), i * 2.0);
  }

  float pattern = vMask * lines;

  float cycleT = fullT * uColorCycleSpeed;
  float rChannel = (pattern + lines * ridge) * (cos(blended.y + cycleT * 0.234) * 0.5 + 1.0);
  float gChannel = (pattern + vMask * ridge) * (sin(blended.x + cycleT * 1.745) * 0.5 + 1.0);
  float bChannel = (pattern + lines * ridge) * (cos(blended.x + cycleT * 0.534) * 0.5 + 1.0);

  vec3 col = (rChannel * uColor1 + gChannel * uColor2 + bChannel * uColor3) * uBrightness;
  float alpha = clamp(length(col), 0.0, 1.0);

  gl_FragColor = vec4(col, alpha);
}
`;

// The prototype's colour1/2/3, as theme tokens.
const COLOR_TOKENS = ["--color-accent", "--color-brand-wave-light", "--color-accent-soft-content"];

// Resolves any CSS colour string to the 0–1 channel triple the shader wants by
// painting it onto a 1×1 canvas, so the token can be written in any syntax.
function colorToVec3(color) {
  const canvas = document.createElement("canvas");
  canvas.width = 1;
  canvas.height = 1;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, 1, 1);
  const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
  return [r / 255, g / 255, b / 255];
}

// Builds the WebGL renderer, shader and canvas inside `el`. Returns null when
// the browser has no WebGL, so the caller keeps the glow fallback.
function createScene(el) {
  let renderer;
  try {
    renderer = new Renderer({ alpha: true, premultipliedAlpha: false });
  } catch {
    return null;
  }
  const gl = renderer.gl;
  gl.clearColor(0, 0, 0, 0);

  const tokens = getComputedStyle(el);
  const [color1, color2, color3] = COLOR_TOKENS.map((name) =>
    colorToVec3(tokens.getPropertyValue(name).trim())
  );

  const program = new Program(gl, {
    vertex: VERTEX,
    fragment: FRAGMENT,
    uniforms: {
      uTime: { value: 0 },
      uResolution: { value: [1, 1, 1] },
      uSpeed: { value: 0.3 },
      uInnerLines: { value: 32.0 },
      uOuterLines: { value: 36.0 },
      uWarpIntensity: { value: 1.0 },
      uRotation: { value: (-45 * Math.PI) / 180 },
      uEdgeFadeWidth: { value: 0.0 },
      uColorCycleSpeed: { value: 1.0 },
      uBrightness: { value: 0.15 },
      uColor1: { value: color1 },
      uColor2: { value: color2 },
      uColor3: { value: color3 },
      // The prototype enables the mouse, but its content layer covers the
      // canvas, so the pointer never reaches it and the warp stays centred.
      uMouse: { value: [0.5, 0.5] },
      uMouseInfluence: { value: 2.0 },
      uEnableMouse: { value: true },
    },
  });
  const mesh = new Mesh(gl, { geometry: new Triangle(gl), program });

  gl.canvas.style.width = "100%";
  gl.canvas.style.height = "100%";
  el.appendChild(gl.canvas);

  return {
    resize(width, height) {
      renderer.setSize(width, height);
      program.uniforms.uResolution.value = [
        gl.canvas.width,
        gl.canvas.height,
        gl.canvas.width / gl.canvas.height,
      ];
    },
    render(time) {
      program.uniforms.uTime.value = time * 0.001;
      renderer.render({ scene: mesh });
    },
    destroy() {
      gl.canvas.remove();
      gl.getExtension("WEBGL_lose_context")?.loseContext();
    },
  };
}

// Draws only while the element has a size. The brand panel is `hidden lg:flex`,
// so on narrow screens the element is 0×0: no WebGL context is created and no
// frame loop runs. The ResizeObserver builds the scene on the first non-zero
// size, starts the loop, and stops it again whenever the box collapses to 0.
const LineWaves = {
  mounted() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let scene = null;
    let frame = 0;

    const update = (time) => {
      frame = requestAnimationFrame(update);
      scene.render(time);
    };
    const start = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    const stop = () => {
      cancelAnimationFrame(frame);
      frame = 0;
    };

    const observer = new ResizeObserver(() => {
      const width = this.el.offsetWidth;
      const height = this.el.offsetHeight;
      if (width === 0 || height === 0) return stop();

      scene ??= createScene(this.el);
      if (!scene) return observer.disconnect(); // no WebGL: keep the glow fallback

      scene.resize(width, height);
      start();
    });
    observer.observe(this.el);

    this.cleanup = () => {
      stop();
      observer.disconnect();
      scene?.destroy();
    };
  },

  destroyed() {
    this.cleanup?.();
  },
};

export default LineWaves;
